import {
	ClientMessageTemplate,
	ClientMessagingOptions,
	ClientMessagingSettings,
	DEFAULT_SETTING,
	MessageChannel,
	UpdateClientMessagingSettingsRequest,
} from "../../../api/client-messaging-settings";

/** What the admin is editing: a client's settings without its id. */
export type MessagingDraft = Omit<ClientMessagingSettings, "sellerId">;

/**
 * Why a draft can't be saved — the same rules the API enforces, checked before the request so the
 * admin sees them next to the setting. Each maps to a string in `clientDetails.messaging.errors`.
 */
export type MessagingDraftError =
	| "costRequired"
	| "pinlessNeedsSent"
	| "noDefaultWithoutPin"
	| "templateUnavailable";

export const toDraft = (settings: ClientMessagingSettings): MessagingDraft => ({
	isMessagingEnabled: settings.isMessagingEnabled,
	messageCost: settings.messageCost,
	requiresDeliveryPin: settings.requiresDeliveryPin,
	messageChannel: settings.messageChannel,
	messageTemplateId: settings.messageTemplateId,
	hideOrderPriceOnTicket: settings.hideOrderPriceOnTicket,
});

/** The channel the client's messages actually go out on. */
export const effectiveChannel = (
	draft: MessagingDraft,
	options: ClientMessagingOptions
): MessageChannel => draft.messageChannel ?? options.defaultChannel;

/** `sent` lets SENT pick, so the template has to exist on both channels it may pick. */
const supportsChannel = (
	template: ClientMessageTemplate,
	channel: MessageChannel
): boolean =>
	channel === "sent"
		? template.channels.includes("sms") &&
		  template.channels.includes("whatsapp")
		: template.channels.includes(channel);

/** On the client's PIN choice and sendable on its channel. */
const matchesPinAndChannel = (
	template: ClientMessageTemplate,
	draft: MessagingDraft,
	options: ClientMessagingOptions
): boolean =>
	template.includesPin === draft.requiresDeliveryPin &&
	supportsChannel(template, effectiveChannel(draft, options));

/** Approved, on the client's PIN choice, and sendable on its channel. */
const fitsDraft = (
	template: ClientMessageTemplate,
	draft: MessagingDraft,
	options: ClientMessagingOptions
): boolean =>
	template.isApproved && matchesPinAndChannel(template, draft, options);

/** The templates the admin can choose for this draft. */
export const templatesFor = (
	draft: MessagingDraft,
	options: ClientMessagingOptions
): ClientMessageTemplate[] =>
	options.templates.filter((template) => fitsDraft(template, draft, options));

/**
 * Applies a change the way the API stores it: turning messaging off puts the PIN back on and shows
 * the ticket price again, and keeps the channel and template. When the admin changes the PIN or the
 * channel and the chosen template can't go with it, the template falls back to the default, in plain
 * sight in its select. Nothing else touches the template: one that stops working for another reason
 * is flagged by `validateDraft` for the admin to replace.
 */
export const applyDraftChange = (
	draft: MessagingDraft,
	patch: Partial<MessagingDraft>,
	options: ClientMessagingOptions
): MessagingDraft => {
	const next: MessagingDraft = { ...draft, ...patch };

	if (patch.isMessagingEnabled === false) {
		next.requiresDeliveryPin = true;
		next.hideOrderPriceOnTicket = false;
		return next;
	}

	const isFitChanged =
		patch.requiresDeliveryPin !== undefined ||
		patch.messageChannel !== undefined;
	const template = options.templates.find(
		(candidate) => candidate.id === next.messageTemplateId
	);
	if (
		isFitChanged &&
		template &&
		!matchesPinAndChannel(template, next, options)
	) {
		next.messageTemplateId = null;
	}

	return next;
};

/** Problems that would make the API refuse the draft. A client with messaging off has none. */
export const validateDraft = (
	draft: MessagingDraft,
	options: ClientMessagingOptions
): MessagingDraftError[] => {
	if (!draft.isMessagingEnabled) return [];

	const errors: MessagingDraftError[] = [];

	if (draft.messageCost === null) errors.push("costRequired");

	if (!draft.requiresDeliveryPin && !options.isSentEnabled) {
		errors.push("pinlessNeedsSent");
	}

	if (draft.messageTemplateId === null) {
		if (
			!draft.requiresDeliveryPin &&
			!options.defaultTemplateWithoutPinId
		) {
			errors.push("noDefaultWithoutPin");
		}
	} else if (
		!templatesFor(draft, options).some(
			(template) => template.id === draft.messageTemplateId
		)
	) {
		errors.push("templateUnavailable");
	}

	return errors;
};

/** Only the settings that changed, or null when nothing did. */
export const buildUpdateRequest = (
	sellerId: number,
	saved: MessagingDraft,
	draft: MessagingDraft
): UpdateClientMessagingSettingsRequest | null => {
	const request: UpdateClientMessagingSettingsRequest = {
		sellerIds: [sellerId],
	};

	if (draft.isMessagingEnabled !== saved.isMessagingEnabled) {
		request.isMessagingEnabled = draft.isMessagingEnabled;
	}
	if (draft.messageCost !== saved.messageCost && draft.messageCost !== null) {
		request.messageCost = draft.messageCost;
	}
	if (draft.requiresDeliveryPin !== saved.requiresDeliveryPin) {
		request.requiresDeliveryPin = draft.requiresDeliveryPin;
	}
	if (draft.hideOrderPriceOnTicket !== saved.hideOrderPriceOnTicket) {
		request.hideOrderPriceOnTicket = draft.hideOrderPriceOnTicket;
	}
	if (draft.messageChannel !== saved.messageChannel) {
		request.messageChannel = draft.messageChannel ?? DEFAULT_SETTING;
	}
	if (draft.messageTemplateId !== saved.messageTemplateId) {
		request.messageTemplateId = draft.messageTemplateId ?? DEFAULT_SETTING;
	}

	return Object.keys(request).length > 1 ? request : null;
};
